import React, { createContext, useContext, useEffect, useState } from 'react';
import { useContextualTour, ContextualTourStep, PageTourConfig } from '@/hooks/useContextualTour';
import { TourSpotlight } from './TourSpotlight';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X, Sparkles } from 'lucide-react';

interface ContextualTourContextValue {
  isActive: boolean;
  currentPage: string;
  currentStep: number;
  completedPages: string[];
  startTour: (page?: string) => void;
  resetAllTours: () => Promise<void>;
}

const ContextualTourContext = createContext<ContextualTourContextValue | null>(null);

export function useContextualTourContext() {
  const context = useContext(ContextualTourContext);
  return context;
}

interface ContextualTourProviderProps {
  children: React.ReactNode;
}

export function ContextualTourProvider({ children }: ContextualTourProviderProps) {
  const tour = useContextualTour();
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);

  // Find target element when step changes
  useEffect(() => {
    if (!tour.isActive || !tour.currentStepData) {
      setTargetElement(null);
      return;
    }

    const findElement = () => {
      const element = document.getElementById(tour.currentStepData!.elementId);
      if (element) {
        setTargetElement(element);
      } else {
        // Retry after a short delay
        setTimeout(findElement, 150);
      }
    };

    const timer = setTimeout(findElement, 200);
    return () => clearTimeout(timer);
  }, [tour.isActive, tour.currentStep, tour.currentStepData]);

  const contextValue: ContextualTourContextValue = {
    isActive: tour.isActive,
    currentPage: tour.currentPage,
    currentStep: tour.currentStep,
    completedPages: tour.completedPages,
    startTour: tour.startTour,
    resetAllTours: tour.resetAllTours
  };

  return (
    <ContextualTourContext.Provider value={contextValue}>
      {children}

      {/* Tour Spotlight and Tooltip */}
      <AnimatePresence>
        {tour.isActive && tour.currentStepData && targetElement && (
          <>
            <TourSpotlight targetElement={targetElement} />
            <ContextualTourTooltip
              step={tour.currentStepData}
              currentStep={tour.currentStep}
              totalSteps={tour.totalSteps}
              pageName={tour.currentPageConfig?.pageName || ''}
              targetElement={targetElement}
              onNext={tour.nextStep}
              onPrev={tour.prevStep}
              onSkip={tour.skipTour}
              isFirst={tour.currentStep === 0}
              isLast={tour.currentStep === tour.totalSteps - 1}
            />
          </>
        )}
      </AnimatePresence>
    </ContextualTourContext.Provider>
  );
}

interface ContextualTourTooltipProps {
  step: ContextualTourStep;
  currentStep: number;
  totalSteps: number;
  pageName: string;
  targetElement: HTMLElement;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  isFirst: boolean;
  isLast: boolean;
}

function ContextualTourTooltip({
  step,
  currentStep,
  totalSteps,
  pageName,
  targetElement,
  onNext,
  onPrev,
  onSkip,
  isFirst,
  isLast
}: ContextualTourTooltipProps) {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const updatePosition = () => {
      const rect = targetElement.getBoundingClientRect();
      const tooltipWidth = 320;
      const tooltipHeight = 200;
      const gap = 16;

      let top = 0;
      let left = 0;

      switch (step.position) {
        case 'top':
          top = rect.top + window.scrollY - tooltipHeight - gap;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
          break;
        case 'bottom':
          top = rect.bottom + window.scrollY + gap;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
          break;
        case 'left':
          top = rect.top + window.scrollY + rect.height / 2 - tooltipHeight / 2;
          left = rect.left - tooltipWidth - gap;
          break;
        case 'right':
          top = rect.top + window.scrollY + rect.height / 2 - tooltipHeight / 2;
          left = rect.right + gap;
          break;
      }

      // Keep within viewport
      left = Math.max(16, Math.min(left, window.innerWidth - tooltipWidth - 16));
      top = Math.max(16, top);

      setPosition({ top, left });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [targetElement, step.position]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: 10 }}
      transition={{ duration: 0.2 }}
      className="fixed z-[10000] w-80 bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
      style={{ top: position.top, left: position.left }}
    >
      {/* Header */}
      <div className="bg-primary/10 px-4 py-3 flex items-center justify-between border-b border-border">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium text-primary">
            Tour: {pageName}
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={onSkip}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {step.title}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {step.description}
        </p>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-muted/50 flex items-center justify-between border-t border-border">
        {/* Progress dots */}
        <div className="flex gap-1.5">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === currentStep
                  ? 'bg-primary'
                  : i < currentStep
                  ? 'bg-primary/50'
                  : 'bg-muted-foreground/30'
              }`}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="flex gap-2">
          {!isFirst && (
            <Button variant="outline" size="sm" onClick={onPrev}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Anterior
            </Button>
          )}
          <Button size="sm" onClick={onNext}>
            {isLast ? 'Concluir' : 'Próximo'}
            {!isLast && <ChevronRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
